import type { PaymentSubState } from '@diamond/shared/types';
import { buyerCaptureException } from '@diamond/shared/utils';

import { formatPaymentSubStateLabel } from './format-payment-sub-state-label';

export type PaymentInstructionSentrySource =
  | 'sticky'
  | 'checkout'
  | 'instruction'
  | 'my_order'
  | 'notification'
  | 'guard';

interface PaymentInstructionSentryContextValues
  extends Record<string, unknown> {
  order_meta_id?: string;
  payment_sub_state?: string;
  reason: string;
  source: PaymentInstructionSentrySource;
  warehouse?: string;
}

export interface PaymentInstructionSentryScope {
  setContext: (
    name: string,
    context: PaymentInstructionSentryContextValues
  ) => void;
  setTag: (key: string, value: string) => void;
}

export interface PaymentInstructionTelemetryContext {
  ffPaymentInstructionPage: boolean;
  orderMetaId: string;
  paymentMethod?: string | null;
  source: PaymentInstructionSentrySource;
  warehouse: string;
}

interface ApplyPaymentInstructionSentryContextArgs {
  orderMetaId?: string;
  paymentSubState?: PaymentSubState;
  reason: string;
  source: PaymentInstructionSentrySource;
  warehouse?: string;
}

export const applyPaymentInstructionSentryContext = (
  scope: PaymentInstructionSentryScope,
  {
    orderMetaId,
    paymentSubState,
    reason,
    source,
    warehouse,
  }: ApplyPaymentInstructionSentryContextArgs
) => {
  const context: PaymentInstructionSentryContextValues = { reason, source };

  if (orderMetaId) context.order_meta_id = orderMetaId;
  if (warehouse) context.warehouse = warehouse;
  if (paymentSubState !== undefined) {
    context.payment_sub_state = formatPaymentSubStateLabel(paymentSubState);
  }

  scope.setTag('feature', 'payment_instruction');
  scope.setTag('source', source);
  scope.setTag('payment_instruction', reason);
  scope.setContext('payment_instruction', context);
};

export const capturePaymentInstructionFailure = (
  error: unknown,
  context: PaymentInstructionTelemetryContext,
  reason: string,
  paymentSubState?: PaymentSubState
) => {
  buyerCaptureException(
    {
      error: error instanceof Error ? error.message : String(error),
      message: 'Payment instruction flow failed',
      reason,
    },
    (scope) => {
      applyPaymentInstructionSentryContext(scope, {
        orderMetaId: context.orderMetaId,
        paymentSubState,
        reason,
        source: context.source,
        warehouse: context.warehouse,
      });
    }
  );
};
